"use client";

import React, { useState, useCallback } from "react";
import {
  DescriptionList,
  type DescriptionItem,
} from "@boostdev/design-system-components";
import { NumberInput } from "@boostdev/design-system-components/client";
import { useClientTranslation } from "@/lib/i18n/client";
import { formatPrice } from "@/lib/shop/money";
import type { ProductWithDetail, ProductVariant } from "@/lib/shop/types";
import { VariantPicker } from "@/app/[locale]/shop/_lib/components/VariantPicker/VariantPicker";
import { AddToCartButton } from "@/app/[locale]/shop/_lib/components/AddToCartButton/AddToCartButton";
import { addToCart } from "@/lib/shop/cart/actions";
import css from "./ProductDetail.module.css";

export interface ProductDetailProps {
  product: ProductWithDetail;
  locale: string;
}

export function ProductDetail({
  product,
  locale,
}: ProductDetailProps): React.JSX.Element {
  const { t } = useClientTranslation(locale, "shop");

  const [selectedVariantId, setSelectedVariantId] = useState<string>(
    product.variants[0].id,
  );
  const [quantity, setQuantity] = useState<number>(1);

  const selectedVariant: ProductVariant =
    product.variants.find((v) => v.id === selectedVariantId) ??
    product.variants[0];

  const isSoldOut = selectedVariant.stock === 0;

  const handleAdd = useCallback(async (): Promise<void> => {
    await addToCart(selectedVariantId, quantity);
    if (typeof window !== "undefined") window.dispatchEvent(new Event("cart:changed"));
  }, [selectedVariantId, quantity]);

  const fulfillmentValue =
    selectedVariant.fulfillmentType === "physical"
      ? t("product.fulfillment.physical")
      : t("product.fulfillment.digital");

  const detailsItems: DescriptionItem[] = [
    ...(selectedVariant.sku
      ? [{ term: t("product.sku"), details: selectedVariant.sku }]
      : []),
    { term: t("product.fulfillmentLabel"), details: fulfillmentValue },
  ];

  return (
    <section className={css.root}>
      <p className={css.price}>
        {formatPrice(selectedVariant.priceCents, locale)}
      </p>

      <VariantPicker
        variants={product.variants}
        selectedId={selectedVariantId}
        onSelect={setSelectedVariantId}
        locale={locale}
      />

      <NumberInput
        label={t("product.quantity")}
        name="quantity"
        value={quantity}
        min={1}
        onChange={setQuantity}
      />

      <AddToCartButton
        disabled={isSoldOut}
        locale={locale}
        onAdd={handleAdd}
      />

      {detailsItems.length > 0 ? (
        <DescriptionList items={detailsItems} />
      ) : null}
    </section>
  );
}
